import { LmsAccount } from "../../../../types/general-types";

export const findNodeById = (tree: LmsAccount[], id: number): LmsAccount | null => {
	let foundNode: LmsAccount | null = null;

	const recurse = (nodes: LmsAccount[]) => {
		for (const node of nodes) {
			if (node.id === id) {
				foundNode = node;
				return;
			}
			if (node.children && node.children.length > 0) {
				recurse(node.children);
			}
		}
	};

	recurse(tree);
	return foundNode;
};

export const findNodeByParentId = (tree: LmsAccount[], parentId: string | null | undefined): LmsAccount | null => {
	let foundNode: LmsAccount | null = null;

	const recurse = (nodes: LmsAccount[]) => {
		for (const node of nodes) {
			if (node.lmsAccountId === parentId) {
				foundNode = node;
				return;
			}
			if (node.children && node.children.length > 0) {
				recurse(node.children);
			}
		}
	};

	recurse(tree);
	return foundNode;
};

export const removeNodeById = (tree: LmsAccount[], treeNode: LmsAccount): LmsAccount[] => {
	const recurse = (nodes: LmsAccount[]): LmsAccount[] => {
		return nodes.filter((node) => {
			if (node.id === treeNode.id) {
				return false;
			}
			if (node.children && node.children.length > 0) {
				node.children = recurse(node.children);
			}
			return true;
		});
	};

	return recurse(tree);
};

export const addNodeToTree = (tree: LmsAccount[], node: LmsAccount): LmsAccount[] => {
	const { parentLmsAccountId } = node;

	if (parentLmsAccountId === null) {
		// If the node has no parent, add it as a root node
		tree.push({ ...node });
		return tree;
	}

	let parentFound = false;

	const addToParent = (nodes: LmsAccount[]): boolean => {
		for (const n of nodes) {
			if (n.lmsAccountId === parentLmsAccountId) {
				if (!n.children) {
					n.children = [];
				}
				n.children.push({ ...node });
				parentFound = true;
				return true;
			}
			if (n.children && n.children.length > 0) {
				if (addToParent(n.children)) {
					return true;
				}
			}
		}
		return false;
	};

	// Attempt to add the node to its parent
	addToParent(tree);

	// If parent was not found, add the node as a root
	if (!parentFound) {
		tree.push({ ...node });
	}

	return tree;
};

export const fixHierarchy = (tree: LmsAccount[]): LmsAccount[] => {
	// Find nodes that are incorrectly placed as roots
	const misplacedNodes: LmsAccount[] = [];

	const findMisplacedNodes = (nodes: LmsAccount[]) => {
		for (const node of nodes) {
			if (node.children && node.children.length > 0) {
				// Recursively check children
				findMisplacedNodes(node.children);
			}
			if (node.parentLmsAccountId !== undefined && node.parentLmsAccountId !== null) {
				// Check if node is incorrectly placed as root
				const parentFound = findNodeByParentId(tree, node.parentLmsAccountId);
				if (
					parentFound !== null &&
					parentFound.children != null &&
					parentFound.children.find((child) => child.lmsAccountId === node.lmsAccountId) === undefined
				) {
					misplacedNodes.push(node);
				}
			}
		}
	};

	// Start checking from the root of the tree
	findMisplacedNodes(tree);

	// Move misplaced nodes to their correct parent
	misplacedNodes.forEach((misplacedNode) => {
		const { lmsAccountId, parentLmsAccountId } = misplacedNode;
		// Find parent node and move the misplaced node under it
		const parent = findNodeByParentId(tree, parentLmsAccountId);
		if (parent) {
			if (!parent.children) {
				parent.children = [];
			}
			parent.children.push(misplacedNode);
			// Remove misplaced node from the root level
			tree = tree.filter((node) => node.lmsAccountId !== lmsAccountId);
		}
	});
	return tree;
};

export const getAllNodeIds = (tree: LmsAccount[]): number[] => {
	const nodeIds: number[] = [];

	const traverseTree = (nodes: LmsAccount[]) => {
		for (const node of nodes) {
			nodeIds.push(node.id); // Collect the ID of the current node
			if (node.children && node.children.length > 0) {
				traverseTree(node.children); // Recursively traverse children
			}
		}
	};

	traverseTree(tree); // Start traversal from the root of the tree
	return nodeIds;
};

export const getAllNodeBySearchString = (tree: LmsAccount[], searchString: string): LmsAccount[] => {
	const filteredNodes: LmsAccount[] = [];

	const traverseTree = (nodes: LmsAccount[], srchStr: string) => {
		for (const node of nodes) {
			if (node.lmsAccountId.includes(srchStr) || node.accountName.includes(srchStr)) {
				filteredNodes.push(node);
			}
			if (node.children && node.children.length > 0) {
				traverseTree(node.children, searchString); // Recursively traverse children
			}
		}
	};

	traverseTree(tree, searchString); // Start traversal from the root of the tree
	return filteredNodes;
};

export const convertListToTree = (data: Array<LmsAccount>): Array<LmsAccount> => {
	const map: { [key: string]: LmsAccount } = {};
	const roots: LmsAccount[] = [];

	// Create a map of accountId to account, and initialize children array
	data.forEach((account) => {
		map[account.lmsAccountId] = { ...account, children: [] };
	});

	// Iterate over the data and build the tree structure
	data.forEach((account) => {
		if (
			account.parentLmsAccountId !== null &&
			account.parentLmsAccountId !== undefined &&
			map[account.parentLmsAccountId]
		) {
			// If parent exists in the map, add this account as a child
			map[account.parentLmsAccountId].children!.push(map[account.lmsAccountId]);
		} else {
			// If no parent or parent doesn't exist in the map, add as a root
			roots.push(map[account.lmsAccountId]);
		}
	});

	return roots;
};
